import React, { createContext, useState, useEffect, useContext, useRef } from 'react'
import PropTypes from 'prop-types'
import { setAuthCallbacks } from '../core/api'

const AuthContext = createContext()

// Where to send unauthenticated users. Override via .env if staffing-app
// moves domains.
const STAFFING_APP_URL =
  import.meta.env.VITE_STAFFING_APP_URL || 'https://www.staffing.edc.design'

// Only the staffing-app may hand ShortCircuit an auth token.
//
// Both listeners below used to accept EDC_AUTH / EDC_AUTH_REFRESH from ANY
// sender and write the token straight into state and localStorage. Any page
// able to reach this window could therefore inject a session, or resolve an
// in-flight refresh with a token of its choosing. The trust was one-sided:
// the parent already does the mirror-image check in staffing-app's
// ShortCircuitEmbed ("if (event.origin !== SC_ORIGIN) return").
//
// Compared as an origin rather than a raw string, so a trailing slash or path
// in VITE_STAFFING_APP_URL cannot silently break the match.
const STAFFING_APP_ORIGIN = (() => {
  try {
    return new URL(STAFFING_APP_URL).origin
  } catch {
    return STAFFING_APP_URL
  }
})()

const isTrustedAuthOrigin = (event) => event.origin === STAFFING_APP_ORIGIN

// How long to wait for a token via postMessage before giving up and
// redirecting to the staffing-app login page.
const TOKEN_WAIT_MS = 4000

// How long a token stored by a previous mount is held back while we ask the
// parent who is signed in now. On a shared browser the stored token can
// belong to the previous user (staffing-app cannot clear SC-origin storage),
// so it is only used if the parent stays silent for this long.
const STORED_TOKEN_GRACE_MS = 1200

// Staffing-app login, sending the user back to the ShortCircuit iframe route
// afterwards. staffing-app's Login.jsx reads `?returnTo=` (and passes it on
// through the Microsoft round trip). returnTo is the staffing-app's
// /shortcircuit route, NOT the SC URL itself: cross-origin localStorage means
// SC can never read a token set by staffing-app, so sending users back here
// would loop. The iframe route hands the token over via postMessage.
const STAFFING_LOGIN_URL = `${STAFFING_APP_URL}/login?returnTo=${encodeURIComponent('/shortcircuit')}`

// Decode the JWT payload without verifying — verification happens
// server-side in the Authorizer Lambda, the client just needs the claims
// for identity (userId, email, name). Manual base64 + JSON to avoid
// pulling in jwt-decode as a runtime dep.
const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') return null
  const segments = token.split('.')
  if (segments.length !== 3) return null
  try {
    // JWT uses base64url; convert to base64 then decode.
    const b64 = segments[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    return JSON.parse(atob(padded))
  } catch (_) {
    return null
  }
}

// Merge JWT claims into the postMessage user object so callers can rely
// on `user.id` / `user.userId` being present even if the parent frame
// didn't include it. EdcAuthLambda's `_jwt.js` puts `userId` in the
// access token payload, so the JWT is the canonical source for ids.
const mergeUserFromToken = (token, userData) => {
  const claims = decodeJwtPayload(token)
  const tokenUser = claims
    ? {
        id: claims.userId || claims.sub || null,
        userId: claims.userId || claims.sub || null,
        email: claims.email || null,
        name: claims.name || null,
        firstName: claims.firstName || null,
        lastName: claims.lastName || null,
      }
    : {}
  // Role and sign-in method always come from the token. The parent sends its
  // cached /auth/me user, which it only reloads on its own bootstrap, while
  // the token is re-signed with the current app_role_id on every refresh.
  // _jwt.js omits appRoleId when the user has no role, so absent means null.
  const tokenAuthoritative = claims
    ? { appRoleId: claims.appRoleId || null, authMethod: claims.authMethod || 'password' }
    : {}
  return {
    ...tokenUser,
    ...(userData || {}),
    ...tokenAuthoritative,
    id: (userData && userData.id) || tokenUser.id,
    userId: (userData && userData.userId) || (userData && userData.id) || tokenUser.userId,
  }
}

const tokenUserId = (token) => {
  const claims = decodeJwtPayload(token)
  return (claims && (claims.userId || claims.sub)) || null
}

// A token we cannot decode, or whose exp has passed, is not worth applying:
// asking the parent gets a fresh one instead of a guaranteed 401/403.
const isUsableToken = (token) => {
  const claims = decodeJwtPayload(token)
  if (!claims) return false
  return typeof claims.exp !== 'number' || claims.exp * 1000 > Date.now()
}

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem('edc_user')
    return raw ? JSON.parse(raw) : null
  } catch (_) {
    return null
  }
}

export const AuthProvider = ({ children }) => {
  const [idToken, setIdToken] = useState(null)
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const timeoutRef = useRef(null)
  const graceRef = useRef(null)
  // Latest user, readable from the long-lived message listeners (their
  // closures would otherwise see the user from the first render).
  const userRef = useRef(null)

  const applyToken = (token, userData) => {
    const merged = mergeUserFromToken(token, userData)
    userRef.current = merged
    setIdToken(token)
    setUser(merged)
    setIsLoading(false)
    // Mirror into localStorage so same-origin reloads can render at once.
    localStorage.setItem('edc_access_token', token)
    localStorage.setItem('edc_user', JSON.stringify(merged))
  }

  // A refreshed token for the session we already have: keep the profile and
  // re-read the claims, so a role changed since the last token (e.g. by Entra
  // role mapping) shows now instead of after the old token's 4h lifetime.
  // If the token belongs to someone else (signed out and back in as another
  // account in the parent), drop the old profile entirely.
  const applyRefreshedToken = (token, userData) => {
    const prev = userRef.current
    const prevId = prev && (prev.userId || prev.id)
    const nextId = tokenUserId(token)
    const sameUser = prev && (!prevId || !nextId || String(prevId) === String(nextId))
    applyToken(token, { ...(sameUser ? prev : {}), ...(userData || {}) })
  }

  // Local teardown only: forget the session without navigating. Used when
  // the parent signs the user out, because the parent owns top-level
  // navigation (it may be sending the user to /login with a return path or
  // a one-shot notice that a second navigation from here would lose).
  const clearSession = () => {
    clearTimeout(graceRef.current)
    graceRef.current = null
    userRef.current = null
    setIdToken(null)
    setUser(null)
    setIsLoading(false)
    localStorage.removeItem('edc_access_token')
    localStorage.removeItem('edc_refresh_token')
    localStorage.removeItem('edc_user')
  }

  // Same window.top / fallback pattern used for the timeout redirect:
  // ensures logout / login-redirect navigates the whole browser, not
  // just the SC iframe (which would re-mount staffing-app inside SC).
  //
  // IMPORTANT: redirect window.top, NOT window — when SC is loaded inside
  // the staffing-app's iframe, redirecting `window.location` navigates only
  // the iframe. The staffing-app then renders its /shortcircuit route inside
  // the iframe → another SC iframe → another timeout → another redirect,
  // stacking a "Back to Hub" bar at every recursion level.
  const navigateTopLevel = (url) => {
    try {
      // Cross-origin parents block reads of `top.location`, but assignment
      // is allowed as long as the assigned URL is well-formed.
      if (window.top && window.top !== window.self) {
        window.top.location.href = url
        return
      }
    } catch (_) {
      // Fall through to in-frame redirect on the rare browsers that
      // disallow the assignment.
    }
    window.location.href = url
  }

  // Logout started inside ShortCircuit (context logout / api.jsx callback).
  const handleLogout = () => {
    clearSession()
    navigateTopLevel(STAFFING_APP_URL)
  }

  useEffect(() => {
    const inIframe = window.parent !== window
    const allowStandalone = import.meta.env.VITE_ALLOW_STANDALONE === 'true'

    // 1. Standalone visit (user opened dev.shortcircuit.edc.design directly).
    //    SC is meant to launch via the staffing-app hub so the AppLauncher
    //    can mint a fresh iframe session. A leftover localStorage token from
    //    a prior iframe mount would let the user bypass the hub forever, so
    //    clear it and bounce to the hub. The hub will re-embed SC and pass
    //    a fresh token via postMessage.
    if (!inIframe && !allowStandalone) {
      localStorage.removeItem('edc_access_token')
      localStorage.removeItem('edc_refresh_token')
      localStorage.removeItem('edc_user')
      window.location.href = `${STAFFING_APP_URL}/shortcircuit`
      return
    }

    // 2. A same-origin token left by the previous mount. Inside the iframe it
    //    is NOT applied straight away: the parent is the source of truth and
    //    may by now be signed in as someone else (shared browser, or signed
    //    out and back in, e.g. with Microsoft), so rendering from it first
    //    could start API calls with the previous user's token. Hold it back
    //    for STORED_TOKEN_GRACE_MS (isLoading stays true, idToken null) and
    //    use it only if the parent stays silent.
    const stored = localStorage.getItem('edc_access_token')
    const hasStoredToken = !!stored && isUsableToken(stored)
    if (stored && !hasStoredToken) {
      localStorage.removeItem('edc_access_token')
      localStorage.removeItem('edc_user')
    }
    if (hasStoredToken && !inIframe) {
      // Standalone (VITE_ALLOW_STANDALONE): no parent to ask.
      applyToken(stored, readStoredUser())
    } else if (hasStoredToken) {
      graceRef.current = setTimeout(() => {
        graceRef.current = null
        if (isUsableToken(stored)) {
          applyToken(stored, readStoredUser())
        } else {
          clearSession()
          navigateTopLevel(STAFFING_LOGIN_URL)
        }
      }, STORED_TOKEN_GRACE_MS)
    }

    // Anything from the parent supersedes a stored token still held back.
    const cancelGrace = () => {
      clearTimeout(graceRef.current)
      graceRef.current = null
    }

    // 3. Always listen to the parent, with or without a stored token:
    //    EDC_AUTH / EDC_AUTH_REFRESH / EDC_LOGOUT must work for the whole
    //    mount. (Returning early on a stored token used to skip this, so the
    //    iframe kept a signed-out or previous user's token until it expired.)
    const handleMessage = (event) => {
      if (!isTrustedAuthOrigin(event)) return
      if (!event.data?.type) return

      if (event.data.type === 'EDC_AUTH' && event.data.token) {
        clearTimeout(timeoutRef.current)
        cancelGrace()
        applyToken(event.data.token, event.data.user)
      }

      // An expired or unreadable refresh is ignored rather than stored: the
      // parent resends its old token when its own /auth/refresh fails.
      if (event.data.type === 'EDC_AUTH_REFRESH' && event.data.token && isUsableToken(event.data.token)) {
        clearTimeout(timeoutRef.current)
        cancelGrace()
        applyRefreshedToken(event.data.token, event.data.user)
      }

      // Parent-initiated sign-out: tear down locally, do not navigate. The
      // parent is already sending the browser where it should go.
      if (event.data.type === 'EDC_LOGOUT') {
        clearTimeout(timeoutRef.current)
        clearSession()
      }
    }

    window.addEventListener('message', handleMessage)

    // Ask parent for the token immediately (staffing-app ShortCircuitEmbed
    // listens for this and responds with EDC_AUTH).
    if (inIframe) {
      window.parent.postMessage({ type: 'SC_REQUEST_AUTH' }, STAFFING_APP_URL)
    }

    // 4. Timeout — no token at all and nothing arrived; send the whole
    // browser (window.top, see navigateTopLevel) to the staffing-app login,
    // which returns to /shortcircuit. Not armed when a stored token exists:
    // a parent that stays silent must not sign out a user who already has a
    // working session (the grace timer above applies it instead).
    if (!hasStoredToken) {
      timeoutRef.current = setTimeout(() => {
        setIsLoading(false)
        navigateTopLevel(STAFFING_LOGIN_URL)
      }, TOKEN_WAIT_MS)
    }

    return () => {
      window.removeEventListener('message', handleMessage)
      clearTimeout(timeoutRef.current)
      clearTimeout(graceRef.current)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // How long to wait for a parent-issued refreshed token before giving up.
  const REFRESH_WAIT_MS = 3000

  const refreshToken = () =>
    new Promise((resolve, reject) => {
      // Standalone (no parent frame) has no refresh source — fail fast so
      // the api.jsx retry path bails out cleanly rather than retrying with
      // the stale token in a loop.
      if (window.parent === window) {
        reject(new Error('No parent frame available to issue a refreshed token'))
        return
      }

      let settled = false
      const cleanup = () => {
        window.removeEventListener('message', onMessage)
        clearTimeout(timer)
      }

      const onMessage = (event) => {
        if (!isTrustedAuthOrigin(event)) return
        // Accept BOTH message types: the staffing-app's ShortCircuitEmbed
        // currently posts the same `EDC_AUTH` envelope for the initial
        // handshake and for refresh responses (its sendAuth() doesn't
        // discriminate). Restricting to only EDC_AUTH_REFRESH meant the
        // refresh promise never resolved, the 3s timeout fired, and we
        // cascaded into a fake "refresh failed → logout" sequence even
        // though the parent did hand us a fresh token.
        if (
          event.data?.type !== 'EDC_AUTH' &&
          event.data?.type !== 'EDC_AUTH_REFRESH'
        ) return
        if (!event.data.token) return
        if (settled) return
        settled = true
        cleanup()
        // The parent resends its old token when its own /auth/refresh fails.
        // An expired or unreadable one would only 403 again, so fail the
        // refresh instead of storing it and resolving with it.
        if (!isUsableToken(event.data.token)) {
          reject(new Error('Parent returned an expired or unreadable token'))
          return
        }
        // Mirror the new token into state + localStorage immediately so the
        // next request after the retry uses the fresh credentials. The
        // always-on handler also writes these — both paths are idempotent.
        setIdToken(event.data.token)
        localStorage.setItem('edc_access_token', event.data.token)
        resolve(event.data.token)
      }

      window.addEventListener('message', onMessage)

      const timer = setTimeout(() => {
        if (settled) return
        settled = true
        cleanup()
        reject(new Error('Token refresh timed out'))
      }, REFRESH_WAIT_MS)

      window.parent.postMessage({ type: 'SC_REQUEST_REFRESH' }, STAFFING_APP_URL)
    })

  // Keep api.jsx callbacks in sync whenever the token changes.
  useEffect(() => {
    setAuthCallbacks(refreshToken, handleLogout)
  }, [idToken]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!idToken,
        user,
        userId: user?.id || user?.userId || null,
        isLoading,
        idToken,
        refreshToken,
        logout: handleLogout,
        loginWithRedirect: () => {
          navigateTopLevel(STAFFING_LOGIN_URL)
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useAuth = () => useContext(AuthContext)
